import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { SessionSummary, Show } from '@/lib/server/shows'
import type { TelescopeNight } from '@/lib/server/telescope'
import type { LogEntry } from '@/lib/server/research'
import type { NightForecast } from '@/components/telescope/night-list'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { ArcDivider, RingsDivider, ScaleDivider } from '@/components/generals/hairline'
import { DomeAscent } from '@/components/landing/dome-ascent'
import { Eyepiece } from '@/components/landing/eyepiece'
import { SkyTonight } from '@/components/sky/sky-tonight'
import { Orrery } from '@/components/orrery/orrery'
import { ShowsSection } from '@/components/shows/shows-section'
import { TelescopeSection } from '@/components/telescope/telescope-section'
import { ResearchStrip } from '@/components/research/research-strip'
import { VisitSection } from '@/components/visit/visit-section'
import { getSessionsBetween, getShows } from '@/lib/server/shows'
import { getUpcomingNights } from '@/lib/server/telescope'
import { forecastsFor } from '@/lib/server/night-forecasts'
import { getLogEntries } from '@/lib/server/research'
import { paymentsEnabled } from '@/lib/server/payments'
import { dateKey, fromLocal, parseDateKey } from '@/lib/time'

type Props = {
    shows: Show[]
    sessions: SessionSummary[]
    nights: TelescopeNight[]
    forecasts: Record<string, NightForecast>
    research: LogEntry[]
    cardPayments: boolean
    todayKey: string
}

const Home: FC<Props> = ({ shows, sessions, nights, forecasts, research, cardPayments, todayKey }): ReactNode => {
    return (
        <Layout>
            <Seo />
            <DomeAscent />
            <div className='container-page pt-10'>
                <ArcDivider label='Hollowmoor Ridge · elevation 420 m' />
            </div>
            <SkyTonight />
            <div className='container-page'>
                <ScaleDivider />
            </div>
            <Eyepiece />
            <div className='container-page pb-16'>
                <RingsDivider />
            </div>
            <Orrery />
            <ShowsSection shows={shows} sessions={sessions} todayKey={todayKey} />
            <div className='container-page'>
                <ArcDivider />
            </div>
            <TelescopeSection nights={nights} forecasts={forecasts} cardPayments={cardPayments} todayKey={todayKey} />
            <div className='container-page'>
                <ScaleDivider />
            </div>
            <ResearchStrip entries={research} />
            <div className='container-page'>
                <RingsDivider />
            </div>
            <VisitSection />
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({ res }) => {
    const todayKey = dateKey(new Date())
    const { year, month, day } = parseDateKey(todayKey)

    res.setHeader('Cache-Control', 'private, no-cache')

    const [shows, sessions, nights, research] = await Promise.allSettled([
        getShows(),
        getSessionsBetween(new Date(), fromLocal(year, month, day + 14, 0, 0)),
        getUpcomingNights(6),
        getLogEntries(3)
    ])

    const failed = [shows, sessions, nights, research].filter(result => result.status === 'rejected')

    failed.forEach(result => {
        if (result.status === 'rejected') console.error('Home page data unavailable:', result.reason instanceof Error ? result.reason.message : result.reason)
    })

    const nightList = nights.status === 'fulfilled' ? nights.value : []
    const forecasts = nightList.length ? await forecastsFor(nightList) : {}

    return {
        props: {
            shows: shows.status === 'fulfilled' ? shows.value : [],
            sessions: sessions.status === 'fulfilled' ? sessions.value : [],
            nights: nightList,
            forecasts,
            research: research.status === 'fulfilled' ? research.value : [],
            cardPayments: paymentsEnabled(),
            todayKey
        }
    }
}

export default Home
